import { AssetsApi } from '@deckee/api-app-main';
import { UpChunk } from '@mux/upchunk';
import { useEffect, useRef, useState } from 'react';
import apiConfiguration from '../../../../services/backend/apiConfiguration';
import { VideoUpload } from '../../../../services/store/modules/incident-report/models';

type VideoUploadData = VideoUpload & {
  progress: number;
  localFilePath?: string;
};

const api = new AssetsApi(apiConfiguration);

interface UseVideoUploadProps {
  onUploadComplete?: (uploadId: string, event: CustomEvent<any>) => void;
  onUploadError?: (uploadId: string, event: CustomEvent<any>) => void;
  onUploadProgress?: (uploadId: string, event: CustomEvent<any>) => void;
  initialVideoUploads?: {
    [key: string]: VideoUploadData;
  };
}

// Provide callback functions for each of the states of the upload. These are here simply to privde a backdoor into the mux library.
// Ideally, the usage will be to await the startUpload function.
const useVideoUpload = ({
  onUploadComplete,
  onUploadError,
  onUploadProgress,
  initialVideoUploads = {},
}: UseVideoUploadProps): {
  uploadInProgress: boolean;
  videoUploads: VideoUploadData[];
  removeVideoByUploadId: (uploadId: string) => void;
  upload: (
    files: FileList | File[],
  ) => Promise<VideoUpload & { localFilePath: string }>;
} => {
  const [videoUploads, setVideoUpload] = useState<{
    [key: string]: VideoUploadData;
  }>(initialVideoUploads);

  // We need to create refs for the callbacks. This is because when the hook is used, the onUpload<X> functions will continually update, and
  // we need to refer to the latest version of it as the events fire. Without this, when there are changes to state in the parent component
  // it will stop update the state
  const handleProgressRef = useRef<UseVideoUploadProps['onUploadProgress']>();
  const handleErrorRef = useRef<UseVideoUploadProps['onUploadError']>();
  const handleCompleteRef = useRef<UseVideoUploadProps['onUploadComplete']>();
  const videoUploadsRef = useRef<{
    [key: string]: VideoUploadData;
  }>();

  // Similar to the refs above, we want to update the ref so that the event listeners for UpChunk can use the latest values
  videoUploadsRef.current = videoUploads;

  // Determine if any of the videos are still uploading and report back to the component
  const uploadInProgress = Object.values(videoUploads).some(
    (video) => video.progress < 100,
  );

  // If any of the passed in callback functions change, update the refs
  useEffect(() => {
    handleProgressRef.current = onUploadProgress;
    handleErrorRef.current = onUploadError;
    handleCompleteRef.current = onUploadComplete;
  }, [onUploadProgress, onUploadError, onUploadComplete]);

  // Remove an uploaded video by the upload id.
  // TODO: Cancel the promise/upload
  const removeVideoByUploadId = (uploadId: string) => {
    const newVideoUploads = { ...videoUploads };
    delete newVideoUploads[uploadId];
    setVideoUpload(newVideoUploads);
  };

  // Initiate an upload of a video to MUX
  const startUpload = (
    files: FileList | File[],
  ): Promise<VideoUpload & { localFilePath: string }> =>
    new Promise((resolve, reject) => {
      let uploadId = '';
      let assetUrl = '';

      const createUpload = async () => {
        const {
          data: { uploadId: apiUploadId, url },
        } = await api.createUploadAsset();
        uploadId = apiUploadId;
        assetUrl = url;
        return url || '';
      };

      if (!files || files.length === 0) {
        reject();
        return;
      }

      const chosenFile = files[0];

      const upload = UpChunk.createUpload({
        endpoint: createUpload,
        file: chosenFile,
        dynamicChunkSize: true,
      });

      // Subscribe to events
      upload.on(
        'error',
        (e) => handleErrorRef.current && handleErrorRef.current(uploadId, e),
      );

      let resolved = false;

      upload.on('progress', (e) => {
        const vids = videoUploadsRef.current || {};
        if (e.detail > 0 && uploadId && !resolved) {
          const url = URL.createObjectURL(chosenFile);

          const video = {
            uploadId,
            url: assetUrl,
            localFilePath: url,
          };
          resolve(video);
          resolved = true;

          // Split the setting of video uploads into two parts. This is because the first time the progress event fires we need
          // to generate a blob to show the preview. Any subsequent times should not do this, because it will cause jank
          setVideoUpload({
            ...vids,
            [uploadId]: {
              ...video,
              uploadId,
              progress: e.detail ? Math.round(e.detail) : 0,
            },
          });
        } else {
          setVideoUpload({
            ...vids,
            [uploadId]: {
              ...vids[uploadId],
              uploadId,
              progress: e.detail ? Math.round(e.detail) : 0,
            },
          });
        }

        if (handleProgressRef.current) {
          handleProgressRef.current(uploadId, e);
        }
      });

      upload.on(
        'success',
        (e) =>
          handleCompleteRef.current && handleCompleteRef.current(uploadId, e),
      );
    });

  return {
    upload: startUpload,
    videoUploads: Object.values(videoUploads),
    removeVideoByUploadId,
    uploadInProgress,
  };
};

export default useVideoUpload;
